import { type FC, type ReactNode, useCallback, useState } from 'react'

import { Button, type ButtonProps } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { DialogProps } from '@/dialogs/types'

export interface Props extends DialogProps {
  title?: string
  desc?: ReactNode
  cancelText?: string
  confirmText?: string
  cancelButtonVariant?: ButtonProps['variant']
  confirmButtonVariant?: ButtonProps['variant']
  confirmButtonTestId?: string
  body?: ReactNode
  hideCancel?: boolean
  customFooter?: ReactNode
  onConfirm?: () => Promise<unknown>
}

export const ConfirmationDialog: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  cancelButtonVariant,
  confirmButtonVariant,
  confirmText,
  cancelText,
  desc,
  body,
  hideCancel,
  customFooter,
  confirmButtonTestId,
}) => {
  const [isConfirming, setIsConfirming] = useState(false)
  const handleConfirm = useCallback(async () => {
    try {
      setIsConfirming(true)
      await onConfirm?.()
      onClose()
    } finally {
      setIsConfirming(false)
    }
  }, [onClose, onConfirm])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      testId="confirmation-dialog"
    >
      <Dialog.Header>
        <Dialog.Title className="w-[462px]">{title ?? 'Confirm'}</Dialog.Title>
        {!!desc && (
          <Dialog.Description className="text-center">
            {desc}
          </Dialog.Description>
        )}
      </Dialog.Header>

      {body && <div className="mt-5">{body}</div>}

      {customFooter ?? (
        <Dialog.Footer className="flex gap-2">
          {!hideCancel && (
            <Button
              onClick={onClose}
              className="flex-1"
              variant={cancelButtonVariant ?? 'outline'}
              testId="cancel-button"
              disabled={isConfirming}
            >
              {cancelText ?? 'Cancel'}
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            className="flex-1"
            variant={confirmButtonVariant}
            testId={confirmButtonTestId}
            isLoading={isConfirming}
          >
            {confirmText ?? 'Confirm'}
          </Button>
        </Dialog.Footer>
      )}
    </Dialog>
  )
}
